import { Alert, Box, Button, Chip, CircularProgress, Radio, Typography } from "@mui/material";
import { useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import AddPaymentMethodModal from "@/components/usersOrgs/AddPaymentMethodModal";
import { useCampaigns } from "@/hooks/useCampaigns";
import { useChangeTopupCard } from "@/hooks/useChangeTopupCard";
import { useAwaitAddedPaymentMethod, usePaymentMethods } from "@/hooks/usePaymentMethods";
import { formatDateLong } from "@/utils/date-format";
import { fundingErrorMessage } from "@/utils/fundingError-helper";

/**
 * Nominate a working card for a campaign that has stopped funding itself.
 *
 * THE ONE SCREEN THE WHOLE RECOVERY PATH DEPENDS ON. Everything else — the three tries, the alert,
 * the strip, the manual retry — assumes a client can replace a dead card themselves. Until this
 * existed they could not: the endpoint was there and nothing in the product reached it.
 *
 * Saving is also the retry, which is why this page shows the outcome of a CHARGE rather than a save
 * confirmation. The client is standing here with their wallet open; a second decline has to reach
 * them now, not in tomorrow's email.
 */
export default function ChangeTopupCard() {
  const { campaignId: param } = useParams();
  const campaignId = Number(param);
  const navigate = useNavigate();

  const { data: campaigns, isLoading: loadingCampaign } = useCampaigns();
  const campaign = useMemo(
    () => campaigns?.find((c) => Number(c.id) === campaignId) ?? null,
    [campaigns, campaignId],
  );

  /**
   * THE CAMPAIGN'S FUNDING GROUP, not the viewer's own placement.
   *
   * `campaignTopupCard` on the API proves the nominated card belongs to the group that funds this
   * campaign, and refuses anything else — so listing the viewer's own cards offered choices the
   * guard was bound to reject, which reached the client as "Forbidden: target out of scope" after
   * they had picked one.
   */
  const fundingGroupId = campaign?.topup?.fundingGroupId ?? null;
  const { data: cards, isLoading: loadingCards } = usePaymentMethods(fundingGroupId);
  const awaitAdded = useAwaitAddedPaymentMethod(fundingGroupId);
  const change = useChangeTopupCard(campaignId);

  const currentCardId = campaign?.topup?.paymentMethodId ?? null;
  const [selected, setSelected] = useState<number | null>(null);
  const [addOpen, setAddOpen] = useState(false);
  const [result, setResult] = useState<{ ok: boolean; message: string } | null>(null);

  async function onSave() {
    if (selected == null) return;
    setResult(null);
    try {
      const retry = await change.mutateAsync(selected);
      // Three outcomes, and a client must be able to tell them apart: it worked, nothing was owed
      // right now, or this card was declined too and they should try another.
      setResult(
        retry?.ok
          ? { ok: true, message: "Card saved, and your campaign has been topped up." }
          : retry?.failure
            ? {
                ok: false,
                message: `Card saved, but the charge was declined: ${retry.failure.message}`,
              }
            : {
                ok: true,
                message: "Card saved. Nothing was owed right now, so you have not been charged.",
              },
      );
    } catch (err) {
      // The server's own words. "Please try again" hid a 403 on a card that belongs to another
      // group, an in-flight call, and a genuine outage behind one sentence that fits none of them.
      setResult({
        ok: false,
        message: fundingErrorMessage(err, "The card could not be saved. Please try again."),
      });
    }
  }

  if (loadingCampaign || loadingCards) return <CircularProgress size={20} sx={{ m: 4 }} />;

  return (
    <Box sx={{ p: { xs: 2, md: 4 }, maxWidth: 640, fontFamily: "var(--font-lato)" }}>
      <Typography sx={{ fontSize: 20, fontWeight: 700, mb: 0.5 }}>Change payment card</Typography>
      <Typography sx={{ fontSize: 14, color: "var(--color-graphite)", mb: 3 }}>
        {campaign ? campaign.name : `Campaign ${campaignId}`}
        {campaign?.endDate ? ` · runs to ${formatDateLong(campaign.endDate)}` : ""}
      </Typography>

      {/* Why they are here, in their own terms. "topup_failure_count = 3" is our vocabulary. */}
      {campaign?.topup?.stopped && (
        <Alert severity="error" sx={{ mb: 3, fontSize: 13 }}>
          This campaign has stopped topping up because its card was declined. Recipients can still
          redeem what it already holds, but it will not add more until a working card is saved here.
        </Alert>
      )}

      {fundingGroupId == null ? (
        // Either the campaign does not fund itself, or the list has not loaded it. Saying so beats
        // an empty card list, which reads as "your group has no cards" and sends the client off to
        // add one that would be refused anyway.
        <Alert severity="info" sx={{ mb: 2, fontSize: 13 }}>
          This campaign does not top itself up, so there is no card to change.
        </Alert>
      ) : cards && cards.length > 0 ? (
        <Box
          role="radiogroup"
          aria-label="Payment card for this campaign's top-ups"
          sx={{ display: "flex", flexDirection: "column", gap: 1, mb: 2 }}
        >
          {cards.map((card) => (
            // A LABEL, not a clickable div. The row used to be a Box with an onClick and a Radio
            // inside it: the pointer worked, the keyboard did not, and a screen reader was handed a
            // radio with no accessible name. As a label wrapping its own input, Tab reaches it,
            // Space selects it, and the card's description is announced with it.
            <Box
              component="label"
              key={card.id}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1.5,
                border: "1px solid",
                borderColor:
                  selected === card.id ? "var(--color-rodeo-black)" : "var(--color-card-border)",
                borderRadius: "8px",
                p: 1.5,
                cursor: "pointer",
                bgcolor: "var(--color-white)",
              }}
            >
              <Radio
                name="topup-card"
                value={String(card.id)}
                checked={selected === card.id}
                onChange={() => setSelected(card.id)}
                size="small"
                inputProps={{
                  "aria-label": `${card.brand ?? "Card"} ending ${card.last4 ?? "unknown"}`,
                }}
              />
              <Typography sx={{ fontSize: 14, flex: 1 }}>
                {card.brand ?? "Card"} ending {card.last4 ?? "????"}
                {card.label ? ` · ${card.label}` : ""}
                {/* WHICH ONE IS IN USE. Asking a client to replace a card without naming the one
                    they are replacing is how you get a "change" that changes nothing. */}
                {card.id === currentCardId && (
                  <Chip size="small" label="Current card" sx={{ ml: 1 }} />
                )}
              </Typography>
              <Typography sx={{ fontSize: 12, color: "var(--color-graphite)" }}>
                {card.expMonth && card.expYear ? `${card.expMonth}/${card.expYear}` : ""}
              </Typography>
            </Box>
          ))}
        </Box>
      ) : (
        <Alert severity="info" sx={{ mb: 2, fontSize: 13 }}>
          This group has no saved cards. Add one to start funding this campaign again.
        </Alert>
      )}

      {result && (
        <Alert severity={result.ok ? "success" : "error"} sx={{ mb: 2, fontSize: 13 }}>
          {result.message}
        </Alert>
      )}

      <Box sx={{ display: "flex", gap: 1 }}>
        <Button variant="outlined" onClick={() => setAddOpen(true)}>
          Add a card
        </Button>
        <Button
          variant="contained"
          onClick={onSave}
          disabled={selected == null || change.isPending}
        >
          {change.isPending
            ? "Saving…"
            : selected != null && selected === currentCardId
              ? "Retry this card"
              : "Save and retry"}
        </Button>
        <Button sx={{ ml: "auto" }} onClick={() => navigate("/campaigns")}>
          Back to campaigns
        </Button>
      </Box>

      <AddPaymentMethodModal
        open={addOpen}
        groupId={fundingGroupId as number}
        onClose={() => setAddOpen(false)}
        // The card arrives on a webhook, so the list has to be waited for rather than refetched
        // once — the same wait the group billing panel uses.
        onAdded={() => {
          setAddOpen(false);
          awaitAdded();
        }}
      />
    </Box>
  );
}
